/**
 * ACCModals — ACC Scope-selection modal + ACC Browser/Version modal for SitesPage.
 *
 * JSX moved verbatim from src/components/SitesPage.tsx. Rendered DOM is byte-equivalent
 * (classNames, text, conditional structure, keys unchanged). All values/handlers arrive
 * as props; accService (isRevitFile) is imported directly — same module singleton.
 */
import React from 'react';
import {
  accService,
  ACCConnection,
  ACCHub,
  ACCProject,
  ACCFolderItem,
  ACCLink,
  ACCFileVersion,
} from '../../services/ACCService';

interface ACCModalsProps {
  accChangeVersionLink: ACCLink | null;
  accConnections: ACCConnection[];
  accConversionType: 'ifc' | 'rvt';
  accDisconnecting: boolean;
  accEnableMasterViews: boolean;
  accFileVersions: ACCFileVersion[];
  accFolderContents: ACCFolderItem[];
  accFolderPath: { id: string; name: string }[];
  accHubs: ACCHub[];
  accLinking: boolean;
  accLoading: boolean;
  accPendingLinkFile: ACCFolderItem | null;
  accProjects: ACCProject[];
  accSelectedConnection: ACCConnection | null;
  accSelectedFileForLink: ACCFolderItem | null;
  accSelectedHub: ACCHub | null;
  accSelectedProject: ACCProject | null;
  accSelectedScope: 'readonly' | 'full';
  accSelectedVersion: number | null;
  accSelectedVersionUrn: string | null;
  accVersionsLoading: boolean;
  accViewDirectMode: boolean;
  showACCBrowserModal: boolean;
  showAccScopeModal: boolean;
  showAccVersionModal: boolean;
  setAccChangeVersionLink: React.Dispatch<React.SetStateAction<ACCLink | null>>;
  setAccEnableMasterViews: React.Dispatch<React.SetStateAction<boolean>>;
  setAccFolderContents: React.Dispatch<React.SetStateAction<ACCFolderItem[]>>;
  setAccFolderPath: React.Dispatch<React.SetStateAction<{ id: string; name: string }[]>>;
  setAccPendingLinkFile: React.Dispatch<React.SetStateAction<ACCFolderItem | null>>;
  setAccSelectedConnection: React.Dispatch<React.SetStateAction<ACCConnection | null>>;
  setAccSelectedFileForLink: React.Dispatch<React.SetStateAction<ACCFolderItem | null>>;
  setAccSelectedHub: React.Dispatch<React.SetStateAction<ACCHub | null>>;
  setAccSelectedProject: React.Dispatch<React.SetStateAction<ACCProject | null>>;
  setAccSelectedScope: React.Dispatch<React.SetStateAction<'readonly' | 'full'>>;
  setAccSelectedVersion: React.Dispatch<React.SetStateAction<number | null>>;
  setAccSelectedVersionUrn: React.Dispatch<React.SetStateAction<string | null>>;
  setAccViewDirectMode: React.Dispatch<React.SetStateAction<boolean>>;
  setShowACCBrowserModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAccScopeModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAccVersionModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleAccScopeConfirm: () => void;
  handleChangeVersionSave: () => void;
  handleVersionSelectAndViewDirect: () => void;
  handleVersionSelectAndLink: () => void;
  handleACCSelectConnection: (connection: ACCConnection) => void;
  handleDisconnectACC: (connection: ACCConnection) => void;
  handleACCAddNewConnection: () => void;
  handleACCSelectHub: (hub: ACCHub) => void;
  handleACCSelectProject: (project: ACCProject) => void;
  handleACCNavigateUp: () => void;
  handleACCNavigateFolder: (folder: ACCFolderItem) => void;
  handleACCViewDirect: (file: ACCFolderItem) => void;
  handleACCLinkFile: (file: ACCFolderItem, conversionType: 'ifc' | 'svf2') => void;
}

const ACCModals: React.FC<ACCModalsProps> = ({
  accChangeVersionLink,
  accConnections,
  accConversionType,
  accDisconnecting,
  accEnableMasterViews,
  accFileVersions,
  accFolderContents,
  accFolderPath,
  accHubs,
  accLinking,
  accLoading,
  accPendingLinkFile,
  accProjects,
  accSelectedConnection,
  accSelectedFileForLink,
  accSelectedHub,
  accSelectedProject,
  accSelectedScope,
  accSelectedVersion,
  accSelectedVersionUrn,
  accVersionsLoading,
  accViewDirectMode,
  showACCBrowserModal,
  showAccScopeModal,
  showAccVersionModal,
  setAccChangeVersionLink,
  setAccEnableMasterViews,
  setAccFolderContents,
  setAccFolderPath,
  setAccPendingLinkFile,
  setAccSelectedConnection,
  setAccSelectedFileForLink,
  setAccSelectedHub,
  setAccSelectedProject,
  setAccSelectedScope,
  setAccSelectedVersion,
  setAccSelectedVersionUrn,
  setAccViewDirectMode,
  setShowACCBrowserModal,
  setShowAccScopeModal,
  setShowAccVersionModal,
  handleAccScopeConfirm,
  handleChangeVersionSave,
  handleVersionSelectAndViewDirect,
  handleVersionSelectAndLink,
  handleACCSelectConnection,
  handleDisconnectACC,
  handleACCAddNewConnection,
  handleACCSelectHub,
  handleACCSelectProject,
  handleACCNavigateUp,
  handleACCNavigateFolder,
  handleACCViewDirect,
  handleACCLinkFile,
}) => {
  return (
    <>
      {/* ACC Scope Selection Modal */}
      {showAccScopeModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ width: '450px', maxWidth: '90vw' }}>
            <div className="modal-content">
              <h2>Connect Autodesk Account</h2>
              <p style={{ color: 'var(--color-text-muted)', marginBottom: '20px' }}>
                Choose the permission level for this connection:
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '16px',
                    border: `2px solid ${accSelectedScope === 'readonly' ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: accSelectedScope === 'readonly' ? 'var(--color-primary-light)' : 'transparent'
                  }}
                  onClick={() => setAccSelectedScope('readonly')}
                >
                  <input
                    type="radio"
                    name="accScope"
                    checked={accSelectedScope === 'readonly'}
                    onChange={() => setAccSelectedScope('readonly')}
                    style={{ marginTop: '2px' }}
                  />
                  <div>
                    <strong>Read-only</strong>
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--color-text-muted)' }}>
                      Browse ACC and view already-synced models.<br />
                      <strong style={{ color: 'var(--color-warning)' }}>Cannot sync new RVT files.</strong>
                    </p>
                  </div>
                </label>

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '16px',
                    border: `2px solid ${accSelectedScope === 'full' ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: accSelectedScope === 'full' ? 'var(--color-primary-light)' : 'transparent'
                  }}
                  onClick={() => setAccSelectedScope('full')}
                >
                  <input
                    type="radio"
                    name="accScope"
                    checked={accSelectedScope === 'full'}
                    onChange={() => setAccSelectedScope('full')}
                    style={{ marginTop: '2px' }}
                  />
                  <div>
                    <strong>Full Access</strong>
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--color-text-muted)' }}>
                      Browse, view, and <strong>sync RVT files</strong> to viewer.<br />
                      Required for translation/sync functionality.
                    </p>
                  </div>
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowAccScopeModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleAccScopeConfirm}
                  disabled={accLoading}
                >
                  {accLoading ? 'Connecting...' : 'Connect to Autodesk'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ACC Browser Modal — includes browsing + version selection as unified flow */}
      {(showACCBrowserModal || (showAccVersionModal && accChangeVersionLink)) && (
        <div className="modal-overlay">
          <div className="modal" style={showACCBrowserModal ? { maxWidth: '680px' } : undefined}>
            <div className="modal-content">

              {/* ── Change Version mode (standalone, no browser) ── */}
              {accChangeVersionLink && !showACCBrowserModal ? (
                <>
                  <h2 style={{ margin: '0 0 4px' }}>Change Version</h2>
                  <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--color-text-muted)' }}>
                    <strong>{accChangeVersionLink.acc_file_name}</strong>
                    <span style={{ margin: '0 6px' }}>&middot;</span>
                    {accChangeVersionLink.acc_project_name}
                    <span style={{ margin: '0 6px' }}>&middot;</span>
                    Currently: {accChangeVersionLink.use_latest_version !== false ? 'Always Latest' : `v${accChangeVersionLink.selected_version_number || '?'}`}
                  </p>

                  {/* Version picker */}
                  {accVersionsLoading ? (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--color-text-muted)' }}>Loading versions...</div>
                  ) : (
                    <>
                      <label
                        style={{
                          display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', marginBottom: '8px',
                          border: `2px solid ${accSelectedVersion === null ? 'var(--color-primary)' : 'var(--color-border)'}`,
                          borderRadius: '6px', cursor: 'pointer',
                          backgroundColor: accSelectedVersion === null ? 'var(--color-primary-light)' : 'transparent'
                        }}
                        onClick={() => { setAccSelectedVersion(null); setAccSelectedVersionUrn(null); }}
                      >
                        <input type="radio" name="accVersionChange" checked={accSelectedVersion === null} onChange={() => { setAccSelectedVersion(null); setAccSelectedVersionUrn(null); }} />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '13px' }}>Always Latest</div>
                          <div style={{ fontSize: '11px', color: 'var(--color-success)' }}>Auto-sync on file changes</div>
                        </div>
                      </label>

                      {/* Filter to show only published versions - sync-only versions share storage with the last publish */}
                      {(() => {
                        const publishedVersions = accFileVersions.filter(v => v.isPublished !== false);
                        const syncOnlyCount = accFileVersions.length - publishedVersions.length;
                        return (
                          <>
                            {syncOnlyCount > 0 && (
                              <div style={{ padding: '8px 12px', backgroundColor: 'var(--color-info-light)', borderRadius: '6px', fontSize: '11px', marginBottom: '8px', color: 'var(--color-text-muted)' }}>
                                {syncOnlyCount} sync-only version{syncOnlyCount > 1 ? 's' : ''} hidden (not published to ACC)
                              </div>
                            )}
                            <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: '6px', marginBottom: '12px' }}>
                              {publishedVersions.map((v, i) => (
                                <label
                                  key={v.versionNumber}
                                  style={{
                                    display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: i < publishedVersions.length - 1 ? '1px solid var(--color-border)' : 'none',
                                    backgroundColor: accSelectedVersion === v.versionNumber ? 'var(--color-primary-light)' : 'transparent'
                                  }}
                                  onClick={() => { setAccSelectedVersion(v.versionNumber); setAccSelectedVersionUrn(v.versionUrn); }}
                                >
                                  <input type="radio" name="accVersionChange" checked={accSelectedVersion === v.versionNumber} onChange={() => { setAccSelectedVersion(v.versionNumber); setAccSelectedVersionUrn(v.versionUrn); }} style={{ marginTop: '3px' }} />
                                  <div style={{ flex: 1, fontSize: '13px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <span>
                                        <strong>v{v.versionNumber}</strong>
                                        {v.versionNumber === accFileVersions[0]?.versionNumber && (
                                          <span style={{ marginLeft: '6px', fontSize: '11px', color: 'var(--color-success)' }}>Latest</span>
                                        )}
                                        {v.publishType && (
                                          <span style={{ marginLeft: '6px', fontSize: '10px', padding: '1px 5px', backgroundColor: 'var(--color-success-light)', borderRadius: '3px', color: 'var(--color-success)' }}>{v.publishType}</span>
                                        )}
                                      </span>
                                      <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                                        {new Date(v.lastModifiedTime).toLocaleDateString()} &middot; {v.lastModifiedUserName}
                                      </span>
                                    </div>
                                    <div style={{ fontSize: '10px', color: 'var(--color-text-muted)', marginTop: '4px', fontFamily: 'monospace' }}>
                                      Size: {v.fileSize ? `${(v.fileSize / 1024 / 1024).toFixed(2)} MB` : 'N/A'} &middot; Storage: {v.storageGuid?.substring(0, 12) || 'N/A'}...
                                    </div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          </>
                        );
                      })()}

                      {accSelectedVersion !== null && (
                        <div style={{ padding: '8px 12px', backgroundColor: 'var(--color-warning-light)', borderRadius: '6px', fontSize: '12px', marginBottom: '12px' }}>
                          Auto-sync will be disabled. Link stays on v{accSelectedVersion}.
                        </div>
                      )}
                    </>
                  )}

                  <div className="modal-actions">
                    <button onClick={() => { setShowAccVersionModal(false); setAccChangeVersionLink(null); }} className="btn btn-secondary">Cancel</button>
                    <button onClick={handleChangeVersionSave} className="btn btn-primary" disabled={accLinking || accVersionsLoading}>
                      {accLinking ? 'Updating...' : 'Update & Re-sync'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* ── ACC Browser flow ── */}

                  {/* Header — adapts to current step */}
                  <h2 style={{ margin: '0 0 16px' }}>
                    {accPendingLinkFile
                      ? (accViewDirectMode ? 'Select Version to View' : 'Select Version to Link')
                      : 'Connect Autodesk Construction Cloud'}
                  </h2>

                  {/* ── Step: Version selection (after picking file + conversion) ── */}
                  {accPendingLinkFile ? (
                    <>
                      <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--color-text-muted)' }}>
                        <strong>{accPendingLinkFile.name}</strong>
                        <span style={{ margin: '0 6px' }}>&middot;</span>
                        {accConversionType === 'ifc' ? 'Convert to IFC' : 'View as RVT (Autodesk Viewer)'}
                      </p>

                      {accVersionsLoading ? (
                        <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--color-text-muted)' }}>Loading versions...</div>
                      ) : (
                        <>
                          <label
                            style={{
                              display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', marginBottom: '8px',
                              border: `2px solid ${accSelectedVersion === null ? 'var(--color-primary)' : 'var(--color-border)'}`,
                              borderRadius: '6px', cursor: 'pointer',
                              backgroundColor: accSelectedVersion === null ? 'var(--color-primary-light)' : 'transparent'
                            }}
                            onClick={() => { setAccSelectedVersion(null); setAccSelectedVersionUrn(null); }}
                          >
                            <input type="radio" name="accVersionBrowser" checked={accSelectedVersion === null} onChange={() => { setAccSelectedVersion(null); setAccSelectedVersionUrn(null); }} />
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '13px' }}>Always Latest</div>
                              <div style={{ fontSize: '11px', color: 'var(--color-success)' }}>Auto-sync on file changes</div>
                            </div>
                          </label>

                          {/* Filter to show only published versions - sync-only versions share storage with the last publish */}
                          {(() => {
                            const publishedVersions = accFileVersions.filter(v => v.isPublished !== false);
                            const syncOnlyCount = accFileVersions.length - publishedVersions.length;
                            return (
                              <>
                                {syncOnlyCount > 0 && (
                                  <div style={{ padding: '8px 12px', backgroundColor: 'var(--color-info-light)', borderRadius: '6px', fontSize: '11px', marginBottom: '8px', color: 'var(--color-text-muted)' }}>
                                    {syncOnlyCount} sync-only version{syncOnlyCount > 1 ? 's' : ''} hidden (not published to ACC)
                                  </div>
                                )}
                                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: '6px', marginBottom: '12px' }}>
                                  {publishedVersions.map((v, i) => (
                                    <label
                                      key={v.versionNumber}
                                      style={{
                                        display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px',
                                        cursor: 'pointer',
                                        borderBottom: i < publishedVersions.length - 1 ? '1px solid var(--color-border)' : 'none',
                                        backgroundColor: accSelectedVersion === v.versionNumber ? 'var(--color-primary-light)' : 'transparent'
                                      }}
                                      onClick={() => { setAccSelectedVersion(v.versionNumber); setAccSelectedVersionUrn(v.versionUrn); }}
                                    >
                                      <input type="radio" name="accVersionBrowser" checked={accSelectedVersion === v.versionNumber} onChange={() => { setAccSelectedVersion(v.versionNumber); setAccSelectedVersionUrn(v.versionUrn); }} style={{ marginTop: '3px' }} />
                                      <div style={{ flex: 1, fontSize: '13px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <span>
                                            <strong>v{v.versionNumber}</strong>
                                            {v.versionNumber === accFileVersions[0]?.versionNumber && (
                                              <span style={{ marginLeft: '6px', fontSize: '11px', color: 'var(--color-success)' }}>Latest</span>
                                            )}
                                            {v.publishType && (
                                              <span style={{ marginLeft: '6px', fontSize: '10px', padding: '1px 5px', backgroundColor: 'var(--color-success-light)', borderRadius: '3px', color: 'var(--color-success)' }}>{v.publishType}</span>
                                            )}
                                          </span>
                                          <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                                            {new Date(v.lastModifiedTime).toLocaleDateString()} &middot; {v.lastModifiedUserName}
                                          </span>
                                        </div>
                                        <div style={{ fontSize: '10px', color: 'var(--color-text-muted)', marginTop: '4px', fontFamily: 'monospace' }}>
                                          Size: {v.fileSize ? `${(v.fileSize / 1024 / 1024).toFixed(2)} MB` : 'N/A'} &middot; Storage: {v.storageGuid?.substring(0, 12) || 'N/A'}...
                                        </div>
                                      </div>
                                    </label>
                                  ))}
                                </div>
                              </>
                            );
                          })()}

                          {/* Revit phases - only for Link mode */}
                          {!accViewDirectMode && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '10px 12px', backgroundColor: 'var(--color-bg-secondary)', borderRadius: '6px', marginBottom: '12px' }}>
                              <input type="checkbox" checked={accEnableMasterViews} onChange={(e) => setAccEnableMasterViews(e.target.checked)} />
                              <div>
                                <div style={{ fontWeight: 500, fontSize: '13px' }}>Enable Revit phases</div>
                                <div style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>Generate phase-based views (Existing, New Construction, etc.)</div>
                              </div>
                            </label>
                          )}

                          {/* Info message based on mode */}
                          {accViewDirectMode ? (
                            <div style={{ padding: '8px 12px', backgroundColor: 'var(--color-success-light)', borderRadius: '6px', fontSize: '12px', marginBottom: '12px' }}>
                              Will view v{accSelectedVersion} directly using ACC derivatives (instant, no download).
                            </div>
                          ) : accSelectedVersion !== null && (
                            <div style={{ padding: '8px 12px', backgroundColor: 'var(--color-warning-light)', borderRadius: '6px', fontSize: '12px', marginBottom: '12px' }}>
                              Auto-sync will be disabled. Link stays on v{accSelectedVersion}.
                            </div>
                          )}
                        </>
                      )}

                      <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
                        <button onClick={() => { setAccPendingLinkFile(null); setAccSelectedFileForLink(null); setAccViewDirectMode(false); }} className="btn btn-secondary">Back</button>
                        {accViewDirectMode ? (
                          <button
                            onClick={handleVersionSelectAndViewDirect}
                            className="btn btn-primary"
                            disabled={accLinking || accVersionsLoading || accSelectedVersion === null}
                            style={{ backgroundColor: '#059669', borderColor: '#059669' }}
                          >
                            {accLinking ? 'Loading...' : 'View Direct'}
                          </button>
                        ) : (
                          <button onClick={handleVersionSelectAndLink} className="btn btn-primary" disabled={accLinking || accVersionsLoading}>
                            {accLinking ? 'Linking...' : 'Link File'}
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* ── Step: Connection / Hub / Project / File browsing ── */}

                      {/* Connection Selection */}
                      {!accSelectedConnection && (
                        <div className="acc-section">
                          <h4 style={{ margin: '0 0 12px' }}>Select Account</h4>
                          {accConnections.length > 0 ? (
                            <div className="acc-list">
                              {accConnections.map(conn => (
                                <div
                                  key={conn.id}
                                  className="acc-list-item"
                                  style={{
                                    padding: '12px 16px', border: '1px solid var(--color-border)', borderRadius: '8px',
                                    marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    cursor: 'pointer', transition: 'border-color 0.15s'
                                  }}
                                  onClick={() => handleACCSelectConnection(conn)}
                                >
                                  <div style={{ flex: 1 }}>
                                    <strong>{conn.acc_account_name}</strong>
                                    <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                                      Connected {new Date(conn.created_at).toLocaleDateString()}
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {conn.scope_level === 'readonly' && (
                                      <span style={{ color: 'var(--color-warning)', fontSize: '12px' }} title="Read-only: Can link files but no 3D viewer (no translation)">View Only</span>
                                    )}
                                    {/* Token Health Indicator */}
                                    {conn.token_health === 'expired' ? (
                                      <span
                                        style={{
                                          color: 'var(--color-danger)',
                                          fontSize: '11px',
                                          fontWeight: 600,
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          backgroundColor: 'rgba(239, 68, 68, 0.1)'
                                        }}
                                        title="Token expired - please reconnect to Autodesk"
                                      >
                                        Reconnect Required
                                      </span>
                                    ) : conn.token_health === 'warning' ? (
                                      <span
                                        style={{
                                          color: 'var(--color-warning)',
                                          fontSize: '11px',
                                          fontWeight: 600,
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          backgroundColor: 'rgba(245, 158, 11, 0.1)'
                                        }}
                                        title={`Token will expire in ${Math.round(14 - (conn.days_since_refresh || 0))} days - auto-refresh scheduled`}
                                      >
                                        Expiring Soon
                                      </span>
                                    ) : conn.is_valid ? (
                                      <span
                                        style={{
                                          color: 'var(--color-success)',
                                          fontSize: '11px',
                                          fontWeight: 600,
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          backgroundColor: 'rgba(34, 197, 94, 0.1)'
                                        }}
                                        title={conn.days_since_refresh !== undefined ? `Token healthy - refreshed ${Math.round(conn.days_since_refresh)} day(s) ago` : 'Token active'}
                                      >
                                        Active
                                      </span>
                                    ) : (
                                      <span
                                        style={{
                                          color: 'var(--color-danger)',
                                          fontSize: '11px',
                                          fontWeight: 600,
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          backgroundColor: 'rgba(239, 68, 68, 0.1)'
                                        }}
                                        title="Token expired - please reconnect"
                                      >
                                        Expired
                                      </span>
                                    )}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleDisconnectACC(conn); }}
                                      className="btn btn-small btn-danger"
                                      disabled={accDisconnecting}
                                      title="Disconnect this Autodesk account"
                                    >
                                      {accDisconnecting ? '...' : 'Disconnect'}
                                    </button>
                                  </div>
                                </div>
                              ))}
                              <button onClick={handleACCAddNewConnection} className="btn btn-secondary" style={{ marginTop: '8px' }}>
                                + Add Another Account
                              </button>
                            </div>
                          ) : (
                            <div>
                              <p>No Autodesk accounts connected.</p>
                              <button onClick={handleACCAddNewConnection} className="btn btn-primary">Connect Autodesk Account</button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Hub Selection */}
                      {accSelectedConnection && !accSelectedHub && (
                        <div className="acc-section">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                            <button onClick={() => setAccSelectedConnection(null)} className="btn btn-small btn-secondary">Back</button>
                            <h4 style={{ margin: 0 }}>Select Hub</h4>
                          </div>
                          {accLoading ? (
                            <p>Loading hubs...</p>
                          ) : accHubs.length === 0 ? (
                            <div style={{ padding: '20px', backgroundColor: 'var(--color-bg-warning)', borderRadius: '8px', border: '1px solid var(--color-warning)' }}>
                              <strong style={{ color: 'var(--color-warning)' }}>No ACC Accounts Found</strong>
                              <p style={{ margin: '10px 0 0 0', fontSize: '14px' }}>
                                Your Autodesk account is connected, but no ACC/BIM360 hubs are accessible.
                              </p>
                              <p style={{ margin: '10px 0 0 0', fontSize: '13px', color: 'var(--color-text-muted)' }}>
                                <strong>To fix this:</strong> An ACC Account Admin must add this app to their account:
                              </p>
                              <ol style={{ margin: '10px 0 0 0', fontSize: '13px', paddingLeft: '20px' }}>
                                <li>Go to ACC Admin &rarr; Settings &rarr; Custom Integrations</li>
                                <li>Click &quot;Add Custom Integration&quot;</li>
                                <li>Enter Client ID: <code style={{ fontSize: '11px', backgroundColor: 'var(--color-bg-secondary)', padding: '2px 4px', borderRadius: '3px' }}>qAqOrtLH2akSSWq8jqMGYJTW9Sd5qYRtnCABYMKl1pFT0dbJ</code></li>
                              </ol>
                            </div>
                          ) : (
                            <div className="acc-list">
                              {accHubs.map(hub => (
                                <div key={hub.id} className="acc-list-item" onClick={() => handleACCSelectHub(hub)}
                                  style={{ padding: '12px 16px', border: '1px solid var(--color-border)', borderRadius: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                                  <strong>{hub.name}</strong>
                                  <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' }}>{hub.type}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Project Selection */}
                      {accSelectedHub && !accSelectedProject && (
                        <div className="acc-section">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                            <button onClick={() => setAccSelectedHub(null)} className="btn btn-small btn-secondary">Back</button>
                            <h4 style={{ margin: 0 }}>Select Project</h4>
                          </div>
                          {accLoading ? (
                            <p>Loading projects...</p>
                          ) : (
                            <div className="acc-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                              {accProjects.map(project => (
                                <div key={project.id} className="acc-list-item" onClick={() => handleACCSelectProject(project)}
                                  style={{ padding: '12px 16px', border: '1px solid var(--color-border)', borderRadius: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                                  {project.name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Folder Browser */}
                      {accSelectedProject && (
                        <div className="acc-section">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                            <button
                              onClick={() => {
                                if (accFolderPath.length > 0) { handleACCNavigateUp(); }
                                else { setAccSelectedProject(null); }
                              }}
                              className="btn btn-small btn-secondary"
                            >Back</button>
                            <h4 style={{ margin: 0 }}>
                              {accSelectedProject.name}
                              {accFolderPath.length > 0 && (
                                <span style={{ fontWeight: 'normal', color: 'var(--color-text-muted)' }}>
                                  {' / '}{accFolderPath.map(f => f.name).join(' / ')}
                                </span>
                              )}
                            </h4>
                          </div>

                          {accLoading ? (
                            <p>Loading...</p>
                          ) : (
                            <div className="acc-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                              {accFolderContents.length === 0 ? (
                                <p style={{ color: 'var(--color-text-muted)' }}>No files or folders</p>
                              ) : (
                                accFolderContents.map(item => (
                                  <div
                                    key={item.id}
                                    className="acc-list-item"
                                    style={{
                                      padding: '10px 16px', border: '1px solid var(--color-border)', borderRadius: '8px',
                                      marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                      cursor: item.type === 'folder' || accService.isRevitFile(item) ? 'pointer' : 'default',
                                      opacity: item.type === 'file' && !accService.isRevitFile(item) ? 0.5 : 1
                                    }}
                                    onClick={() => { if (item.type === 'folder') handleACCNavigateFolder(item); }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span>{item.type === 'folder' ? '📁' : accService.isRevitFile(item) ? '🏗️' : '📄'}</span>
                                      <span>{item.name}</span>
                                      {accService.isRevitFile(item) && (
                                        <span style={{ color: 'var(--color-success)', fontSize: '11px', fontWeight: 600 }}>(Revit)</span>
                                      )}
                                    </div>
                                    {accService.isRevitFile(item) && (
                                      <div style={{ position: 'relative' }}>
                                        {accSelectedFileForLink?.id === item.id ? (
                                          <div style={{
                                            display: 'flex', gap: '4px', padding: '6px',
                                            backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border)',
                                            borderRadius: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                          }}>
                                            <button onClick={(e) => { e.stopPropagation(); handleACCViewDirect(item); }}
                                              className="btn btn-small" disabled={accLinking}
                                              style={{ fontSize: '12px', backgroundColor: '#059669', borderColor: '#059669', color: 'white' }}
                                              title="View directly using ACC derivatives (instant, no download)">
                                              {accLinking ? '...' : 'View Direct'}
                                            </button>
                                            <button onClick={(e) => { e.stopPropagation(); handleACCLinkFile(item, 'svf2'); }}
                                              className="btn btn-secondary btn-small" disabled={accLinking} style={{ fontSize: '12px' }}
                                              title="Link & sync RVT file (downloads to server)">
                                              {accLinking ? '...' : 'Link RVT'}
                                            </button>
                                            <button onClick={(e) => { e.stopPropagation(); handleACCLinkFile(item, 'ifc'); }}
                                              className="btn btn-small" disabled={accLinking} style={{ fontSize: '12px' }}
                                              title="Link & convert to IFC (downloads and converts)">
                                              {accLinking ? '...' : 'Link IFC'}
                                            </button>
                                            <button onClick={(e) => { e.stopPropagation(); setAccSelectedFileForLink(null); }}
                                              className="btn btn-small" style={{ fontSize: '11px', padding: '2px 6px' }}>
                                              ✕
                                            </button>
                                          </div>
                                        ) : (
                                          <button onClick={(e) => { e.stopPropagation(); setAccSelectedFileForLink(item); }}
                                            className="btn btn-primary btn-small" disabled={accLinking}>
                                            {accLinking ? 'Linking...' : 'Select'}
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="modal-actions">
                        <button
                          onClick={() => {
                            setShowACCBrowserModal(false);
                            setAccSelectedConnection(null);
                            setAccSelectedHub(null);
                            setAccSelectedProject(null);
                            setAccFolderPath([]);
                            setAccFolderContents([]);
                          }}
                          className="btn btn-secondary"
                        >Close</button>
                      </div>
                    </>
                  )}
                </>
              )}

            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ACCModals;
